import type { LeadStageKey } from '@crm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export const LEADS_ROUTES = {
  BOARD: '/operacao/funil',
  CONVERSION: '/relatorios/conversao',
} as const;

const ENDPOINT = '/api/v1/leads';

export interface Lead {
  id: string;
  unitId: string;
  unitName: string;
  conversationId: string | null;
  customerId: string | null;
  contactPhone: string;
  displayName: string;
  customerImage: string | null;
  stage: LeadStageKey;
  status: 'OPEN' | 'WON' | 'LOST';
  campaignId: string | null;
  ownerId: string | null;
  ownerName: string | null;
  lastMessage: string | null;
  /** Quem escreveu a ultima mensagem - o cliente ou o salao. */
  lastMessageInbound: boolean;
  /** Cliente perguntou e ninguem respondeu ainda; automatica nao conta. */
  waitingReply: boolean;
  enteredStageAt: string;
  createdAt: string;
}

export interface BoardFilters {
  unitId?: string;
  /** Quadro de uma campanha so - e por aqui que Resultados chega ao funil. */
  campaignId?: string;
}

export interface FunnelRow {
  stage: LeadStageKey;
  total: number;
}

function toSearch(params: Record<string, string | undefined>) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value) search.set(key, value);

  return search.toString();
}

export function useLeadBoard(filters: BoardFilters) {
  return useQuery({
    queryKey: ['lead-board', filters],
    queryFn: () => apiClient.get<{ data: Lead[] }>(`${ENDPOINT}/board?${toSearch({ ...filters })}`),
    refetchInterval: 15_000,
  });
}

export function useMoveLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, stage }: { id: string; stage: LeadStageKey }) =>
      apiClient.patch<Lead>(`${ENDPOINT}/${id}/stage`, { stage }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['lead-board'] }),
  });
}

export interface ManualMessageInput {
  unitId: string;
  customerId?: string;
  name?: string;
  phone?: string;
  body: string;
}

export function useManualMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ManualMessageInput) =>
      apiClient.post<{ lead: Lead; conversationId: string; messageId: string }>(
        `${ENDPOINT}/manual-message`,
        input,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-board'] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

export function useFunnel(params: {
  from?: string;
  to?: string;
  unitId?: string;
  campaignId?: string;
  /** Varias campanhas de uma vez, separadas por virgula. */
  campaignIds?: string;
  /** De onde o lead veio: sozinho, ou puxado por um disparo. */
  trigger?: 'organic' | 'campaign';
  /** Etapa que abre o funil: so a coorte que passou por ela e contada. */
  startStage?: LeadStageKey;
}) {
  return useQuery({
    queryKey: ['funnel', params],
    queryFn: () =>
      apiClient.get<{ data: FunnelRow[]; contacts: number; attended: number }>(
        `/api/v1/reports/funnel?${toSearch(params)}`,
      ),
  });
}

export function useMonthlyFunnel(params: { from: string; to: string; campaignId?: string }) {
  return useQuery({
    queryKey: ['funnel-monthly', params],
    queryFn: () =>
      apiClient.get<{ data: { month: string; entered: number; scheduled: number }[] }>(
        `/api/v1/reports/funnel/monthly?${toSearch(params)}`,
      ),
  });
}
